"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KetteArtNrGenerator = void 0;
const echain_shared_i18n_1 = require("@igus/echain-shared-i18n");
const echain_shared_validation_1 = require("@igus/echain-shared-validation");
const Kette_1 = require("./Kette");
const KetteArtNrSplitter_1 = require("./KetteArtNrSplitter");
const KetteUtil_1 = require("./KetteUtil");
const KetteList_1 = require("./KetteList");
const ArticleUtils_1 = require("../ArticleUtils");
/**
 * The `KetteArtNrGenerator` has the responsibility to
 * generate existing or invented ArtNr according to provided kette details
 */
class KetteArtNrGenerator {
    constructor(logger) {
        this.logger = logger;
        this.className = 'KetteArtNrGenerator';
        this.ketteArtNrSplitter = new KetteArtNrSplitter_1.KetteArtNrSplitter();
    }
    /*
     * Generates a valid GeneratedKetteArtNr based of the provided kettenList and ArtNr.
     * @returns The generated ArtNr with some meta information
     */
    generateWithExistingArtNr(availableKetten, artNr) {
        var _a, _b;
        if (!artNr) {
            throw new Error(`${this.className}.generateWithExistingArtNr(): artNr not set`);
        }
        const finalKetteJson = availableKetten.find((kette) => echain_shared_i18n_1.LocaleService.getArtNr(kette.ArtNr) === artNr);
        if (!finalKetteJson) {
            throw new Error(`${this.className}.generateWithExistingArtNr(): artNr '${artNr}' not in ketteList`);
        }
        if (!((_a = finalKetteJson.SerieRef) === null || _a === void 0 ? void 0 : _a.Kuerzel)) {
            throw new Error(`${this.className}.generateWithExistingArtNr(): Kette has not serieRef.kuerzel`);
        }
        const finalKette = Kette_1.Kette.fromJson(finalKetteJson);
        const generationPropertiesExist = {
            serieKuerzel: !!((_b = finalKette.serieRef) === null || _b === void 0 ? void 0 : _b.kuerzel),
            indexBreite: !!finalKette.indexBreite,
            indexR: !!finalKette.indexBiegeradiusR,
            indexRbr: !!finalKette.indexBiegeradiusRBR,
        };
        finalKette.artNrExists = true;
        finalKette.artNrGenerated = artNr;
        return {
            kette: finalKette,
            generationValid: true,
            generationProperties: {
                artNrCountry: echain_shared_i18n_1.LocaleService.getArtNrCountry(),
                serieRefKuerzel: finalKette.serieRef.kuerzel,
                indexBreite: finalKette === null || finalKette === void 0 ? void 0 : finalKette.indexBreite,
                indexR: finalKette === null || finalKette === void 0 ? void 0 : finalKette.indexBiegeradiusR,
            },
            generationPropertiesExist,
        };
    }
    /*
     * Generates a valid ArtNr on base of the provided ketten array.
     * @param ketteList The available ketten to use as base of the generation
     * @param generationProperties The properties used for generation
     * @returns The generated ArtNr with some meta information
     */
    generate(availableKetten, gp) {
        if (!gp.indexFarbe)
            gp.indexFarbe = '0';
        const serieRefKuerzel = gp.serieRefKuerzel;
        const indexBreite = gp.indexBreite;
        const indexR = gp.indexR;
        const indexRbr = gp.indexRbr;
        const indexFarbe = gp.indexFarbe;
        const material = gp.material;
        const logPrefix = `${this.className}.generate():`;
        echain_shared_validation_1.Validation.throws([
            echain_shared_validation_1.Validator.truthy(availableKetten.length, `${logPrefix} ketteList: `),
            echain_shared_validation_1.Validator.truthy(indexBreite, `${logPrefix} indexBreite: `),
            echain_shared_validation_1.Validator.truthy(indexR, `${logPrefix} indexR: `),
        ]);
        // filter list by kuerzel
        const ketteListByKuerzel = KetteList_1.KetteList.filterByKuerzel(availableKetten, serieRefKuerzel !== null && serieRefKuerzel !== void 0 ? serieRefKuerzel : '');
        if (ketteListByKuerzel.length === 0) {
            this.logger.warn(`${logPrefix} Cannot find kuerzel`, serieRefKuerzel);
            return this.handleUnknownGeneration({ serieKuerzel: false }, availableKetten, gp, []);
        }
        // filter list by indexBreite
        const ketteListByIndexBreite = KetteList_1.KetteList.filterByIndexBreite(ketteListByKuerzel, indexBreite);
        if (ketteListByIndexBreite.length === 0) {
            this.logger.warn(`${logPrefix} Cannot find indexBreite`, indexBreite);
            return this.handleUnknownGeneration({ serieKuerzel: true, indexBreite: false }, availableKetten, gp, ketteListByKuerzel);
        }
        // filter list by indexBiegeradius Radius
        const ketteListByIndexR = KetteList_1.KetteList.filterByIndexBiegeradiusR(ketteListByIndexBreite, indexR);
        if (ketteListByIndexR.length === 0) {
            this.logger.warn(`${logPrefix} Cannot find indexR`, indexR);
            return this.handleUnknownGeneration({ serieKuerzel: true, indexBreite: true, indexR: false }, availableKetten, gp, ketteListByIndexBreite);
        }
        let finalKetteList = ketteListByIndexR;
        // filter list by indexBiegeradius Rückwärtiger Biegeradius
        if (indexRbr) {
            const ketteListByIndexRbr = KetteList_1.KetteList.filterByIndexBiegeradiusRbr(ketteListByIndexR, indexRbr);
            if (ketteListByIndexRbr.length === 0) {
                this.logger.warn(`${logPrefix} Cannot find indexRbr`, indexRbr);
                return this.handleUnknownGeneration({ serieKuerzel: true, indexBreite: true, indexR: true, indexRbr: false }, availableKetten, gp, ketteListByIndexR);
            }
            finalKetteList = ketteListByIndexRbr;
        }
        // just pick the first entry of our final-filtered-kette-list..
        // even if there are still multiple results..
        // artNr: "2700.05.063.0"
        // artNr: "2700.05.063.0.ESD"
        // so lets just return the first occurence since we assume that its the most generic one
        const finalKetteJson = finalKetteList.at(0);
        if (!finalKetteJson) {
            throw new Error(`${logPrefix}: finalKette is undefined.`);
        }
        const finalKette = Kette_1.Kette.fromJson(finalKetteJson);
        finalKette.artNrExists = true;
        finalKette.artNrGenerated = this.generateExistingArtNr(gp.artNrCountry, finalKette, indexFarbe, material);
        this.patchKette(finalKette, gp, finalKetteList);
        const generationPropertiesExist = {
            serieKuerzel: true,
            indexBreite: true,
            indexR: true,
            indexRbr: Boolean(indexRbr),
        };
        return {
            kette: finalKette,
            generationValid: true,
            generationProperties: gp,
            generationPropertiesExist,
        };
    }
    /**
     * Returns the localized reference article number for the given ketteJson
     */
    getLocalizedPriceReferenceArtNrFromKetteJson(ketteJson, country) {
        const artNrCountry = echain_shared_i18n_1.LocaleUtil.findArtNrCountry(country);
        if (!ketteJson.SerieRef || !ketteJson.SerieRef.Kuerzel || !ketteJson.IndexBreite) {
            if (ketteJson.ArtNr && ketteJson.ArtNr[artNrCountry])
                return ketteJson.ArtNr[artNrCountry];
            if (ketteJson.ArtNr && ketteJson.ArtNr['DE'])
                return ketteJson.ArtNr['DE'];
            throw new Error(`${this.className}.getLocalizedPriceReferenceArtNrFromKetteJson(): Could not determine any localized artNr`);
        }
        return `${ketteJson.SerieRef.Kuerzel}${echain_shared_i18n_1.LocaleUtil.getArtNrSeparator(artNrCountry)}${ketteJson.IndexBreite}`;
    }
    handleUnknownGeneration(generationPropertiesExist, availableKetten, generationProperties, referenceKetten) {
        const kettenToUse = referenceKetten.length ? referenceKetten : availableKetten;
        const artNr = this.generateInventedArtNr(kettenToUse, generationProperties);
        const kette = Kette_1.Kette.fromInventedArtNr({ ArtNrGenerated: artNr });
        this.patchKette(kette, generationProperties, referenceKetten);
        // artNr already exists but we could not map it using the properties.
        // There is something wrong with the data, so lets log a warning and mark the generation as
        // invalid.
        if (availableKetten.some((k) => ArticleUtils_1.ArticleUtils.getQuoteArtNr(k, generationProperties.artNrCountry) === artNr)) {
            this.logger.warn(`${this.className}.handleUnknownGeneration(): Invented ArtNr already exists`, artNr);
            return {
                kette,
                generationValid: false,
                generationProperties,
                generationPropertiesExist,
                referenceKetten,
            };
        }
        // artNr is really invented.
        // We do not create a kette obj here because we don't know what the app wants to do.
        // The app should handle it on its own.
        return {
            kette,
            generationValid: true,
            generationProperties,
            generationPropertiesExist,
            referenceKetten,
        };
    }
    generateInventedArtNr(availableKetten, gp) {
        var _a;
        const serieRefKuerzel = (_a = gp.serieRefKuerzel) !== null && _a !== void 0 ? _a : '';
        const ketteJson = KetteUtil_1.KetteUtil.getKetteBySerieKuerzel(availableKetten, serieRefKuerzel);
        const separator = echain_shared_i18n_1.LocaleUtil.getArtNrSeparator(gp.artNrCountry);
        let serieKuerzel = ketteJson
            ? this.ketteArtNrSplitter.extractSerieKuerzel(gp.artNrCountry, Kette_1.Kette.fromJson(ketteJson))
            : serieRefKuerzel;
        serieKuerzel = echain_shared_i18n_1.LocaleUtil.isUsArtNr(gp.artNrCountry) ? serieKuerzel.replace('.', '-') : serieKuerzel;
        let artNr = `${serieKuerzel}${separator}${gp.indexBreite}${separator}${gp.indexR}`;
        if (gp.indexRbr)
            artNr += `/${gp.indexRbr}`;
        if (gp.indexFarbe)
            artNr += `${separator}${gp.indexFarbe}`;
        const artNrHasPlusSign = KetteUtil_1.KetteUtil.artNrHasPlusSignBySerieKuerzel(availableKetten, serieRefKuerzel, gp.artNrCountry);
        if (artNrHasPlusSign) {
            artNr += `${separator}+`;
        }
        if (gp.material)
            artNr += `${separator}${gp.material}`;
        return artNr;
    }
    generateExistingArtNr(artNrCountry, kette, color, material) {
        let artNr = this.getKetteArtNr(artNrCountry, kette);
        if (!artNr) {
            throw new Error(`${this.className}.generateExistingArtNr(): Kette has no artNr`);
        }
        const separator = echain_shared_i18n_1.LocaleUtil.getArtNrSeparator(artNrCountry);
        const separatorAndPlus = `${separator}+`;
        const artNrIncludesPlus = artNr.indexOf(separatorAndPlus) !== -1;
        const colorPart = this.ketteArtNrSplitter.extractColor(artNrCountry, kette);
        const materialPart = this.ketteArtNrSplitter.extractMaterial(artNrCountry, kette);
        if (materialPart) {
            let subStrEnd = artNr.length - materialPart.length - separator.length;
            if (artNrIncludesPlus)
                subStrEnd -= separatorAndPlus.length;
            artNr = artNr.substring(0, subStrEnd);
        }
        if (colorPart) {
            let subStrEnd = artNr.length - colorPart.length - separator.length;
            if (artNrIncludesPlus)
                subStrEnd -= separatorAndPlus.length;
            artNr = artNr.substring(0, subStrEnd);
        }
        if (color)
            artNr += `${separator}${color}`;
        if (artNrIncludesPlus)
            artNr += separatorAndPlus;
        if (material)
            artNr += `${separator}${material}`;
        else if (materialPart)
            artNr += `${separator}${materialPart}`;
        return artNr;
    }
    getKetteArtNr(artNrCountry, kette) {
        var _a, _b;
        return (_a = kette.artNr) !== null && _a !== void 0 ? _a : (_b = kette.localizedArtNr) === null || _b === void 0 ? void 0 : _b[artNrCountry];
    }
    patchKette(kette, gp, referenceKetten) {
        // patch in indexBreite
        if (gp.indexBreite) {
            kette.indexBreite = gp.indexBreite;
        }
        // patch in R of indexBiegeradius
        if (gp.indexR) {
            kette.indexBiegeradius = gp.indexR;
        }
        // patch in RBR of indexBiegeradius
        if (gp.indexRbr && kette.indexBiegeradius && !kette.indexBiegeradius.includes('/')) {
            kette.indexBiegeradius += `/${gp.indexRbr}`;
        }
        // patch in farbe
        if (gp.indexFarbe) {
            kette.color = gp.indexFarbe;
        }
        // patch in material
        if (gp.material) {
            kette.material = gp.material;
        }
        // try to find the best suited referenceJson
        // .. this may be overwritten by `patchReferenceKette` later on!
        const ketteReferenceJson = this.tryFindBestReferenceJson(gp, referenceKetten);
        if (ketteReferenceJson) {
            // kette.serieRef could be {} is not valid
            const hasSerieRef = Boolean(Object.keys(kette.serieRef).length);
            // patch SerieRef from reference kette if exists
            if (!hasSerieRef && ketteReferenceJson.SerieRef) {
                kette.serieRef = { kuerzel: ketteReferenceJson.SerieRef.Kuerzel, id: ketteReferenceJson.SerieRef.Id };
            }
            // patch SerieRef from generation params
            if (!hasSerieRef && gp.serieRefKuerzel) {
                kette.serieRef = { kuerzel: gp.serieRefKuerzel };
            }
            kette.referenceKetteJson = ketteReferenceJson;
        }
        return kette;
    }
    tryFindBestReferenceJson(gp, referenceKetten) {
        const referenceKetteListLength = referenceKetten.length;
        if (referenceKetteListLength === 0)
            return undefined;
        if (referenceKetteListLength === 1)
            return referenceKetten.at(0);
        let ketten = referenceKetten;
        if (gp.material) {
            const kettenWithSameMaterial = ketten.filter((k) => k.Material === gp.material);
            if (kettenWithSameMaterial.length > 0)
                ketten = kettenWithSameMaterial;
        }
        if (gp.indexFarbe) {
            const kettenWithSameFarbe = ketten.filter((k) => k.Color === gp.indexFarbe);
            if (kettenWithSameFarbe.length > 0)
                ketten = kettenWithSameFarbe;
        }
        return ketten.at(0);
    }
}
exports.KetteArtNrGenerator = KetteArtNrGenerator;
//# sourceMappingURL=KetteArtNrGenerator.js.map